import {
  Cascader,
  Checkbox,
  ColorPicker,
  DatePicker,
  Divider,
  Form,
  FormItemProps,
  Input,
  InputNumber,
  Radio,
  Rate,
  Select,
  Slider,
  TimePicker,
  Transfer,
  TreeSelect,
  Typography,
} from 'antd'
import React, { ReactNode, useMemo } from 'react'
import { styled } from 'styled-components'
import { Toolbox } from '~/plugins/toolbox/shared'
import { FormInputType, StepType } from '../../../common/types'
import { useIfConditions } from '../../hooks/useIfConditions'
import { InputUpload } from '../CustomInputs/InputUpload'
import { ProviderAccountAuthorization } from '../CustomInputs/ProviderAccountAuthorization'
import { RadioCard } from '../CustomInputs/RadioCard'
import { RRulePicker } from '../CustomInputs/RRulePicker'
import { SelectGooglePicker } from '../CustomInputs/SelectGooglePicker'
import { SelectOneDrivePicker } from '../CustomInputs/SelectOneDrivePicker'
import { SelectProviderAccount } from '../CustomInputs/SelectProviderAccount'
import { SelectProviderAction } from '../CustomInputs/SelectProviderAction'
import { SwitchWrapped } from '../CustomInputs/SwitchWrapped'
import { TagWall } from '../CustomInputs/TagWall'
import { FormItemWithIndications } from './components/FormItem'

const DYNAMIC_INPUTS = [
  'SelectProviderAction',
  'GooglePicker',
  'ProviderAccountAuthorization',
  'OneDrivePicker',
]

const FormItemWrapper = styled(Form.Item)`
  margin: 0;

  .ant-form-item-control-input {
    min-height: 0;
  }

  .ant-form-item-control-input-content {
    > * {
      padding-bottom: 24px;
    }
  }
`

type Props = {
  keyDynamic?: number
  isReadonly?: boolean
  step: StepType
  formInput: FormInputType
  formValuesConfiguration: any
  formValues: any
  onUploadingChange?: (isUploading: boolean) => void
}

export const ViewerFormInput: React.FC<Props> = ({
  keyDynamic,
  isReadonly = false,
  step,
  formInput,
  formValuesConfiguration,
  formValues,
  onUploadingChange,
}) => {
  const ifConditionsHook = useIfConditions()
  const inputType = formInput.inputType
  const ifConditions = formInput.ifConditions
  const disabledConditions = formInput.disabledConditions

  const {
    providerAccountIdKey,
    providerAccountScopeKeys = [],
    ...inputProps
  }: any = {
    disabled: isReadonly,
    ...formInput.propsFormInput,
  }

  const isInputHidden = (formValuesCurrent: any, inputValue: any) => {
    const isHidden = isReadonly && Toolbox.Utility.isNull(inputValue)

    if (isHidden) {
      return true
    }

    const formValuesAll = { ...formValues, ...formValuesCurrent }
    const isVisible = ifConditionsHook.run(formValuesAll, ifConditions)

    if (!isVisible) {
      return true
    }

    return false
  }

  const isInputDisabled = (formValuesCurrent: any) => {
    const isDisabled = disabledConditions
      ? ifConditionsHook.run(formValuesCurrent, disabledConditions)
      : inputProps.disabled

    return isDisabled
  }

  const shouldUpdateInput: FormItemProps['shouldUpdate'] = (
    valuesBefore,
    valuesAfter,
  ) => {
    const valueBefore = valuesBefore[step.key]?.[formInput.key]
    const valueAfter = valuesAfter[step.key]?.[formInput.key]
    const hiddenBefore = isInputHidden(valuesBefore, valueBefore)
    const hiddenAfter = isInputHidden(valuesAfter, valueAfter)
    const disabledBefore = isInputDisabled(valuesBefore)
    const disabledAfter = isInputDisabled(valuesAfter)
    const shouldUpdateNow =
      valueBefore !== valueAfter ||
      hiddenBefore !== hiddenAfter ||
      disabledBefore !== disabledAfter

    return shouldUpdateNow
  }

  const shouldUpdateProviderAction: FormItemProps['shouldUpdate'] = (
    valuesBefore,
    valuesAfter,
    source,
  ) => {
    if (!providerAccountIdKey) {
      return false
    }

    const valuesBeforeFlat = Toolbox.Utility.flattenObject(valuesBefore)
    const valuesAfterFlat = Toolbox.Utility.flattenObject(valuesAfter)

    const isProviderAccountIdUpdated =
      valuesBefore[providerAccountIdKey] !== valuesAfter[providerAccountIdKey]

    const isInputUpdated = shouldUpdateInput(valuesBefore, valuesAfter, source)

    Toolbox.Utility.arrayUnique

    const isScopeUpdated = providerAccountScopeKeys.some(
      key =>
        valuesBeforeFlat[key]?.[0]?.isCorrupted !==
        valuesAfterFlat[key]?.[0]?.isCorrupted,
    )

    const shouldUpdateNow =
      isProviderAccountIdUpdated || isInputUpdated || isScopeUpdated

    return shouldUpdateNow
  }

  if (DYNAMIC_INPUTS.includes(inputType)) {
    return (
      <>
        <FormItemWrapper shouldUpdate={shouldUpdateProviderAction}>
          {formInstance => {
            const names = providerAccountIdKey?.split('.') ?? []
            const providerAccountId = formInstance.getFieldValue(names)

            if (Toolbox.Utility.isNull(providerAccountId)) {
              return <></>
            }

            const value = formInstance.getFieldValue([step.key, formInput.key])
            const values = formInstance.getFieldsValue()
            const isHidden = isInputHidden(values, value)

            if (isHidden) {
              return <></>
            }

            const scopeNames = providerAccountScopeKeys.map(key =>
              key.split('.'),
            )

            const providerAccountScopeValues = scopeNames
              .map(names => formInstance.getFieldValue(names)?.[0])
              .filter(Boolean)

            const hasScopeEmpty = providerAccountScopeValues.some(values =>
              Toolbox.Utility.isNull(values),
            )

            if (hasScopeEmpty) {
              return <></>
            }

            const isDisabled = isInputDisabled(values)

            return (
              <>
                <InputMapped
                  inputProps={{
                    ...inputProps,
                    disabled: isDisabled,
                  }}
                  step={step}
                  keyDynamic={keyDynamic}
                  providerAccountId={providerAccountId}
                  providerAccountScopeValues={providerAccountScopeValues}
                  formInput={formInput}
                  formValuesConfiguration={formValuesConfiguration}
                  onUploadingChange={onUploadingChange}
                />
              </>
            )
          }}
        </FormItemWrapper>
      </>
    )
  }

  return (
    <>
      <FormItemWrapper shouldUpdate={shouldUpdateInput}>
        {options => {
          const value = options.getFieldValue([step.key, formInput.key])
          const values = options.getFieldsValue()
          const isHidden = isInputHidden(values, value)

          if (isHidden) {
            return <></>
          }

          const isDisabled = isInputDisabled(values)

          return (
            <InputMapped
              inputProps={{
                ...inputProps,
                disabled: isDisabled,
              }}
              step={step}
              keyDynamic={keyDynamic}
              formInput={formInput}
              formValuesConfiguration={formValuesConfiguration}
              onUploadingChange={onUploadingChange}
            />
          )
        }}
      </FormItemWrapper>
    </>
  )
}

type PropsMapped = {
  keyDynamic?: number
  step: StepType
  formInput: FormInputType
  inputProps: any
  formValuesConfiguration: any
  providerAccountId?: string
  providerAccountScopeValues?: string[]
  onUploadingChange?: (isUploading: boolean) => void
}

const InputMapped: React.FC<PropsMapped> = ({
  keyDynamic,
  step,
  formInput,
  inputProps,
  formValuesConfiguration,
  providerAccountId,
  providerAccountScopeValues = [],
  onUploadingChange,
}) => {
  const inputType = formInput.inputType
  const { rules, ...propsFormItem } = formInput.propsFormItem ?? {}

  const getRulesForInput = () => {
    switch (inputType) {
      case 'ProviderAccountAuthorization':
        return [
          {
            validator: (_, value) =>
              value?.[0]?.isCorrupted ? Promise.reject() : Promise.resolve(),
            message: 'Authorization is not valid',
          },
        ]
      default:
        return []
    }
  }

  const rulesEnsured = [
    ...(formInput.propsFormItem?.rules ?? []),
    ...getRulesForInput(),
  ]
    .filter(rule => rule && Object.keys(rule).length > 0)
    .map(rule => ({
      ...rule,
      message: rule['message'] ?? 'This field is required',
    }))

  const InputFound = useMemo(() => {
    const store: Record<FormInputType['inputType'], ReactNode> = {
      Input: <Input {...inputProps} />,
      Textarea: <Input.TextArea {...inputProps} />,
      Select: <Select {...inputProps} />,
      Upload: (
        <InputUpload
          {...inputProps}
          formValuesConfiguration={formValuesConfiguration}
          onUploadingChange={onUploadingChange}
        />
      ),
      InputNumber: <InputNumber {...inputProps} />,
      Switch: <SwitchWrapped {...inputProps} />,
      DatePicker: <DatePicker {...inputProps} />,
      Checkbox: <Checkbox.Group {...inputProps} />,
      Radio: <Radio.Group {...inputProps} />,
      Rate: <Rate {...inputProps} />,
      Slider: <Slider {...inputProps} />,
      TimePicker: <TimePicker {...inputProps} />,
      Transfer: <Transfer {...inputProps} />,
      TreeSelect: <TreeSelect {...inputProps} />,
      Cascader: <Cascader {...inputProps} />,
      RadioCard: <RadioCard {...inputProps} />,
      TagWall: <TagWall {...inputProps} />,
      SelectProviderAccount: (
        <SelectProviderAccount
          {...inputProps}
          formValuesConfiguration={formValuesConfiguration}
        />
      ),
      ProviderAccountAuthorization: null,
      RRulePicker: (
        <RRulePicker
          {...inputProps}
          formValuesConfiguration={formValuesConfiguration}
        />
      ),
      ColorPicker: <ColorPicker {...inputProps} showText />,
      SelectProviderAction: null,
      GooglePicker: null,
      OneDrivePicker: null,
      Text: <Typography.Text {...inputProps} />,
      Title: <Typography.Title {...inputProps} />,
      Paragraph: <Typography.Paragraph {...inputProps} />,
      Divider: <Divider {...inputProps} />,
      Dynamic: null,
    }

    const InputMapped = store[inputType] ?? store.Input

    return InputMapped
  }, [formInput, inputProps, formValuesConfiguration, onUploadingChange])

  const getValueFromEvent = () => {
    switch (inputType) {
      case 'ColorPicker':
        return (color: any) => color.toHexString()
      case 'RRulePicker':
        return (items: any[]) => items?.[0]
      case 'ProviderAccountAuthorization':
        return (items: any[]) => items?.[0]
    }
  }

  if (DYNAMIC_INPUTS.includes(inputType)) {
    return (
      <>
        <>
          <FormItemWithIndications
            {...propsFormItem}
            name={[keyDynamic ?? step.key, formInput.key]}
            rules={rulesEnsured}
            propsIndication={formInput.propsIndication}
          >
            {inputType === 'SelectProviderAction' && (
              <SelectProviderAction
                {...inputProps}
                formValuesConfiguration={formValuesConfiguration}
                providerAccountId={providerAccountId}
                providerAccountScopeValues={providerAccountScopeValues}
              />
            )}

            {inputType === 'GooglePicker' && (
              <SelectGooglePicker
                {...inputProps}
                formValuesConfiguration={formValuesConfiguration}
                providerAccountId={providerAccountId}
                providerAccountScopeValues={providerAccountScopeValues}
              />
            )}

            {inputType === 'OneDrivePicker' && (
              <SelectOneDrivePicker
                {...inputProps}
                formValuesConfiguration={formValuesConfiguration}
                providerAccountId={providerAccountId}
                providerAccountScopeValues={providerAccountScopeValues}
              />
            )}

            {inputType === 'ProviderAccountAuthorization' && (
              <ProviderAccountAuthorization
                {...inputProps}
                formValuesConfiguration={formValuesConfiguration}
                providerAccountId={providerAccountId}
              />
            )}
          </FormItemWithIndications>
        </>
      </>
    )
  }

  return (
    <>
      <FormItemWithIndications
        {...propsFormItem}
        name={[keyDynamic ?? step.key, formInput.key]}
        getValueFromEvent={getValueFromEvent()}
        rules={rulesEnsured}
        propsIndication={formInput.propsIndication}
      >
        {InputFound}
      </FormItemWithIndications>
    </>
  )
}
